import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  crawlSiteUrl,
  errorMessage,
  fetchCompanyByVat,
  fetchCompanyIndicators,
  fetchSuggestions,
  findCompanies,
  generateBusinessReport,
  type BusinessReportCompetitor,
  type CompanyCandidate,
  type CompanyProfile,
  type MarketCompetitor,
  type PageSuggestion,
} from './api';
import PageBack from './PageBack';
import { MetricBars, ZoneVisual } from './MarketVisuals';
import { CategoryIcon } from './InsightVisuals';
import CompanyHistory from './CompanyHistory';
import OfficialDocuments, { OfficialDocumentsWithoutVat } from './OfficialDocuments';
import CatastoProperty from './CatastoProperty';
import { saveDownloadedFile } from './downloadFile';
import { forgetActivityCompany, readRememberedActivityCompany, rememberActivityCompany } from './activityCompanyMemory';

const integer = new Intl.NumberFormat('it-IT');
const currency = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
type ReportLens = 'acquisizione' | 'apertura';

function setParam(params: URLSearchParams, key: string, value: string | number | null) {
  if (value != null && `${value}`.trim()) params.set(key, `${value}`);
}

interface LocalBusinessContext {
  competitors?: number | null;
  radiusMeters?: number | null;
  targetId?: string | null;
  lat?: number | null;
  lng?: number | null;
}

export function localBusinessDetailUrl(item: MarketCompetitor, context: LocalBusinessContext = {}) {
  const params = new URLSearchParams();
  setParam(params, 'name', item.name);
  setParam(params, 'category', item.primaryType);
  setParam(params, 'rating', item.rating);
  setParam(params, 'reviews', item.reviewsCount);
  setParam(params, 'distance', item.distanceMeters);
  setParam(params, 'address', item.address);
  setParam(params, 'website', item.website);
  setParam(params, 'maps', item.mapsUrl);
  setParam(params, 'competitors', context.competitors ?? null);
  setParam(params, 'radius', context.radiusMeters ?? null);
  setParam(params, 'target', context.targetId ?? null);
  setParam(params, 'lat', context.lat ?? null);
  setParam(params, 'lng', context.lng ?? null);
  return `/competitor/locale?${params.toString()}`;
}

function numberParam(value: string | null) {
  if (value == null || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDistance(value: number | null) {
  if (value == null) return 'Distanza non disponibile';
  if (value >= 1_000) return `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(value / 1_000)} km`;
  return `${integer.format(Math.round(value))} m`;
}

function readableCategory(value: string | null) {
  if (!value) return 'Attività locale';
  const normalized = value.replace(/_/g, ' ').trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function normalizeName(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function suggestionScore(name: string, suggestion: PageSuggestion) {
  const expected = normalizeName(name);
  const candidate = normalizeName(suggestion.pageName);
  if (!expected || !candidate) return 0;
  if (expected === candidate) return 1;
  if (expected.includes(candidate) || candidate.includes(expected)) return .86;
  const expectedTokens = new Set(expected.split(' ').filter((token) => token.length > 1));
  const candidateTokens = new Set(candidate.split(' ').filter((token) => token.length > 1));
  const overlap = [...expectedTokens].filter((token) => candidateTokens.has(token)).length;
  return overlap / Math.max(expectedTokens.size, candidateTokens.size, 1);
}

function plausibleMetaMatch(name: string, rows: PageSuggestion[]) {
  return rows
    .map((row) => ({ row, score: suggestionScore(name, row) }))
    .filter((item) => item.score >= .6)
    .sort((a, b) => b.score - a.score || b.row.adsCount - a.row.adsCount)[0]?.row ?? null;
}

function splitSignals(value: string | null) {
  return value?.split(',').map((item) => item.trim()).filter(Boolean) ?? [];
}

function reportCompetitorsFromState(state: unknown, subjectName: string): BusinessReportCompetitor[] | undefined {
  if (!state || typeof state !== 'object' || !('competitors' in state) || !Array.isArray(state.competitors)) return undefined;
  const subject = normalizeName(subjectName);
  const seen = new Set<string>();
  const competitors = state.competitors.reduce<BusinessReportCompetitor[]>((rows, value) => {
    if (!value || typeof value !== 'object' || !('name' in value) || typeof value.name !== 'string') return rows;
    const competitorName = value.name.trim();
    const key = normalizeName(competitorName);
    if (!key || key === subject || seen.has(key)) return rows;
    seen.add(key);
    const rating = 'rating' in value && typeof value.rating === 'number' && Number.isFinite(value.rating) ? value.rating : null;
    const reviewsCount = 'reviewsCount' in value && typeof value.reviewsCount === 'number' && Number.isFinite(value.reviewsCount) ? value.reviewsCount : null;
    rows.push({ name: competitorName, rating, reviewsCount });
    return rows;
  }, []);
  return competitors.length > 0 ? competitors : undefined;
}

function DossierIcon() {
  return <svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M13 5h15l8 8v30H13z"/><path d="M28 5v9h9M19 23h11M19 29h11M19 35h7"/><path d="m8 15-3 3 3 3"/></svg>;
}

interface RegistryCandidateCardProps {
  candidate: CompanyCandidate;
  selected: boolean;
  allowActivityCheck: boolean;
  cachedProfile?: CompanyProfile;
  onSelect: () => void;
  onVerified: (profile: CompanyProfile) => void;
}

function RegistryCandidateCard({ candidate, selected, allowActivityCheck, cachedProfile, onSelect, onVerified }: RegistryCandidateCardProps) {
  const verification = useMutation({
    mutationFn: () => fetchCompanyByVat(candidate.vat),
    onSuccess: onVerified,
  });
  const profile = cachedProfile ?? verification.data;
  const locationMeta = [candidate.town, candidate.registeredYear ? `iscritta dal ${candidate.registeredYear}` : null].filter(Boolean).join(' · ') || 'Località e anno non disponibili';

  return <article className={`local-company-candidate-item${selected ? ' is-selected' : ''}${candidate.sedeCoincide === true ? ' is-address-match' : ''}`} role="listitem">
    <button type="button" className={`local-company-candidate${selected ? ' is-selected' : ''}`} aria-pressed={selected} onClick={onSelect}>
      <span className="local-candidate-choice" aria-hidden="true">{selected ? '✓' : ''}</span>
      <span className="local-candidate-main">
        <strong>{candidate.companyName}</strong>
        <span className="local-candidate-location">
          <small>{locationMeta}</small>
          {candidate.street && <span className="local-candidate-street"><span>Sede: {candidate.street}</span></span>}
          {candidate.sedeCoincide === true && <span className="local-candidate-address-state is-match">Sede = indirizzo attività</span>}
          {candidate.sedeCoincide === false && <span className="local-candidate-address-state">Sede diversa dall’indirizzo attività</span>}
        </span>
        <span>P.IVA {candidate.vat}</span>
      </span>
      <span className={`local-candidate-score${candidate.score < .5 ? ' is-weak' : ''}`}>{Math.round(candidate.score * 100)}%<small>{candidate.score < .5 ? 'debole' : 'affinità'}</small></span>
    </button>
    {allowActivityCheck && <div className="local-candidate-activity">
      {!profile && <button type="button" disabled={verification.isPending} onClick={() => verification.mutate()}>{verification.isPending ? 'Verifico l’attività dichiarata…' : 'Verifica attività dichiarata (€0,10)'}</button>}
      {verification.isError && <p className="local-candidate-activity-error"><span>{errorMessage(verification.error)}</span><button type="button" onClick={() => verification.mutate()}>Riprova</button></p>}
      {profile && <div className="local-candidate-activity-result" role="status"><span>Attività dichiarata</span><strong>{profile.atecoDescription || 'Descrizione ATECO non disponibile'}</strong><small>{profile.atecoCode ? `ATECO ${profile.atecoCode}` : 'Codice non disponibile'}</small></div>}
    </div>}
  </article>;
}

export default function LocalBusinessDetail() {
  const location = useLocation();
  const [params] = useSearchParams();
  const name = params.get('name')?.trim() ?? '';
  const category = params.get('category');
  const rating = numberParam(params.get('rating'));
  const reviews = numberParam(params.get('reviews'));
  const distance = numberParam(params.get('distance'));
  const address = params.get('address');
  const website = params.get('website');
  const mapsUrl = params.get('maps');
  const competitors = numberParam(params.get('competitors'));
  const radius = numberParam(params.get('radius'));
  const lat = numberParam(params.get('lat'));
  const lng = numberParam(params.get('lng'));
  const targetId = params.get('target');
  const rememberedCompany = useMemo(() => readRememberedActivityCompany(name, address), [name, address]);
  const [selectedCandidate, setSelectedCandidate] = useState<CompanyCandidate | null>(rememberedCompany?.candidate ?? null);
  const [confirmedVat, setConfirmedVat] = useState<string | null>(rememberedCompany?.vat ?? null);
  const [candidateProfiles, setCandidateProfiles] = useState<Record<string, CompanyProfile>>({});
  const [reportLens, setReportLens] = useState<ReportLens | null>(null);
  const meta = useQuery({
    queryKey: ['local-business-meta-match', name],
    queryFn: () => fetchSuggestions(name),
    enabled: name.length >= 2,
    staleTime: 5 * 60_000,
  });
  const metaMatch = useMemo(() => plausibleMetaMatch(name, meta.data ?? []), [meta.data, name]);
  const site = useMutation({ mutationFn: () => crawlSiteUrl(website!) });
  const siteVat = site.data?.vatNumber?.trim() || null;
  const vat = siteVat || confirmedVat;
  const company = useMutation({
    mutationFn: async (confirmedCompanyVat: string) => {
      const profile = candidateProfiles[confirmedCompanyVat] ?? await fetchCompanyByVat(confirmedCompanyVat);
      const indicators = await fetchCompanyIndicators(confirmedCompanyVat);
      return { profile, indicators };
    },
  });
  const registry = useMutation({
    mutationFn: () => findCompanies({ name, lat, lng, radiusMeters: radius, address }),
    onMutate: () => {
      setSelectedCandidate(null);
      setConfirmedVat(null);
      setCandidateProfiles({});
      company.reset();
    },
  });
  const dossier = useMutation({
    mutationFn: generateBusinessReport,
    onSuccess: saveDownloadedFile,
  });
  useEffect(() => {
    const remembered = readRememberedActivityCompany(name, address);
    setSelectedCandidate(remembered?.candidate ?? null);
    setConfirmedVat(remembered?.vat ?? null);
    setCandidateProfiles({});
    setReportLens(null);
    site.reset();
    registry.reset();
    company.reset();
    dossier.reset();
  }, [location.search]);
  useEffect(() => {
    if (!siteVat) return;
    rememberActivityCompany({ activityName: name, address, vat: siteVat, companyName: site.data?.legalName });
  }, [address, name, site.data?.legalName, siteVat]);
  const registryCandidates = useMemo(() => [...(registry.data ?? [])].sort((a, b) => Number(b.sedeCoincide === true) - Number(a.sedeCoincide === true) || b.score - a.score), [registry.data]);
  const plausibleRegistryCandidates = registryCandidates.filter((candidate) => candidate.score >= .5);
  const needsActivityVerification = plausibleRegistryCandidates.length >= 2;
  const weakRegistryMatch = registryCandidates.length > 0 && registryCandidates.every((candidate) => candidate.score < .5);
  const techSignals = splitSignals(site.data?.techPixels ?? null);
  const noDigitalPresence = meta.isSuccess && !metaMatch && !website;
  const advertisingIntensity = techSignals.length > 0 ? 'Alta' : metaMatch ? 'Presente' : meta.isSuccess ? 'Non rilevata' : 'Analisi in corso';
  const cachedCompanyProfile = vat ? candidateProfiles[vat] : undefined;
  const reportCompetitors = useMemo(() => reportCompetitorsFromState(location.state, name), [location.state, name]);
  const createDossier = () => {
    if (!reportLens) return;
    dossier.mutate({
      name,
      lens: reportLens,
      category,
      rating,
      reviewsCount: reviews,
      distanceMeters: distance,
      address,
      website,
      mapsUrl,
      competitorsInRadius: competitors,
      radiusMeters: radius,
      vat: vat ?? undefined,
      metaPageId: metaMatch?.pageId,
      zoneTargetId: targetId,
      competitors: reportCompetitors,
    });
  };
  const chooseReportLens = (lens: ReportLens) => {
    setReportLens(lens);
    dossier.reset();
  };
  const confirmSelectedCompany = () => {
    if (!selectedCandidate) return;
    setConfirmedVat(selectedCandidate.vat);
    rememberActivityCompany({ activityName: name, address, vat: selectedCandidate.vat, companyName: selectedCandidate.companyName, candidate: selectedCandidate });
    company.reset();
  };
  const changeConfirmedCompany = () => {
    forgetActivityCompany(name, address);
    setConfirmedVat(null);
    setSelectedCandidate(null);
    company.reset();
  };

  if (!name) return <section className="empty local-business-missing"><PageBack fallback="/competitor"/><p className="eyebrow">Dettaglio attività</p><h1>Attività non disponibile</h1><p>Il collegamento non contiene i dati minimi per ricostruire il profilo locale.</p><Link className="btn-primary" to="/competitor">Torna all’analisi</Link></section>;

  return <article className="local-business-detail">
    <PageBack fallback="/competitor" label="Torna ai competitor" />
    <header className="local-business-hero">
      <CategoryIcon category={category ?? name} className="local-business-symbol"/>
      <div><p className="eyebrow">Dettaglio attività</p><h1>{name}</h1><p>{readableCategory(category)} · {formatDistance(distance)}</p></div>
      <div className="local-business-hero-signal"><span className="local-business-kind">Attività locale</span><MetricBars value={reviews ?? 0} max={Math.max(reviews ?? 0, 100)} label={`${reviews ?? 0} recensioni`}/></div>
    </header>

    <dl className="local-business-facts">
      <div><dt>Rating Google</dt><dd>{rating != null ? `★ ${rating.toFixed(1)}` : 'n/d'}</dd></div>
      <div><dt>Recensioni</dt><dd>{reviews != null ? integer.format(reviews) : 'n/d'}</dd></div>
      <div><dt>Distanza</dt><dd>{formatDistance(distance)}</dd></div>
    </dl>

    <section className="local-business-address" aria-labelledby="local-address-title"><div><p className="eyebrow">Posizione</p><h2 id="local-address-title">{address || 'Indirizzo non disponibile'}</h2></div><div>{website && <a href={website} target="_blank" rel="noreferrer">Sito ↗</a>}{mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer">Google Maps ↗</a>}</div></section>

    <section className="local-business-overview" aria-label="Quadro sintetico dell’attività"><dl><div><dt>Concorrenti nel raggio</dt><dd>{competitors != null ? integer.format(competitors) : 'Non disponibili'}</dd></div><div><dt>Intensità pubblicitaria</dt><dd>{advertisingIntensity}</dd></div><div><dt>Presenza online</dt><dd>{website ? 'Sito disponibile' : metaMatch ? 'Profilo pubblicitario' : 'Solo scheda locale'}</dd></div></dl></section>

    {radius != null && <section className="local-business-map-preview" aria-labelledby="local-map-preview-title"><div><p className="eyebrow">Area osservata</p><h2 id="local-map-preview-title">Attività nel suo contesto</h2></div><ZoneVisual compact radiusLabel={formatDistance(radius)} title={address || name} markers={Math.max(3, Math.min(7, competitors ?? 3))}/><Link className="btn-secondary" to={targetId ? `/mappa?target=${encodeURIComponent(targetId)}` : '/mappa'}>Vedi mappa completa <span aria-hidden="true">↗</span></Link></section>}

    {noDigitalPresence && <aside className="local-business-physical"><span aria-hidden="true">○</span><div><strong>Attività solo fisica</strong><p>Nessun sito né profilo pubblicitario plausibile trovato. Anche l’assenza di una presenza digitale è un segnale utile.</p></div></aside>}

    <section className="local-detail-section" aria-labelledby="local-meta-title">
      <header><div><p className="eyebrow">Archivio pubblico</p><h2 id="local-meta-title">Fa pubblicità?</h2></div><span className="local-free-label">Automatico · gratis</span></header>
      {meta.isPending && <div className="local-detail-loading" role="status"><span className="sr-only">Cerco il profilo pubblicitario</span><i/><i/></div>}
      {meta.isError && <div className="notice notice-error"><p>{errorMessage(meta.error)}</p><button type="button" className="btn-secondary" onClick={() => meta.refetch()}>Riprova</button></div>}
      {meta.isSuccess && metaMatch && <div className="local-meta-match"><div><strong>{metaMatch.pageName}</strong><span>{integer.format(metaMatch.adsCount)} annunci nell’archivio</span></div><Link className="btn-primary" to={`/competitor/${encodeURIComponent(metaMatch.pageId)}`} state={{ from: `${location.pathname}${location.search}`, backLabel: name }}>Vedi il suo profilo pubblicitario <span aria-hidden="true">→</span></Link></div>}
      {meta.isSuccess && !metaMatch && <p className="local-honest-state">Nessun profilo Meta sufficientemente simile al nome dell’attività. Non vengono proposti abbinamenti deboli.</p>}
    </section>

    <section className="local-detail-section" aria-labelledby="local-site-title">
      <header><div><p className="eyebrow">Presenza proprietaria</p><h2 id="local-site-title">Analizza il sito</h2></div>{website && <button type="button" className="btn-secondary" disabled={site.isPending} onClick={() => site.mutate()}>{site.isPending ? 'Analizzo (~20s)…' : site.data ? 'Aggiorna analisi' : 'Analizza il sito'}</button>}</header>
      {!website && <p className="local-honest-state">Nessun sito associato alla scheda Google. Il crawl non può essere avviato.</p>}
      {website && !site.data && !site.isPending && <p className="local-section-intro">L’analisi legge homepage e pagine pubbliche per rilevare offerta, prezzi, strumenti pubblicitari, social e identità fiscale. Parte solo quando scegli di avviarla.</p>}
      {site.isPending && <div className="local-detail-loading" role="status"><span className="sr-only">Analisi del sito in corso</span><i/><i/><i/></div>}
      {site.isError && <div className="notice notice-error"><p>{errorMessage(site.error)}</p><button type="button" className="btn-secondary" onClick={() => site.mutate()}>Riprova</button></div>}
      {site.data && <div className="local-site-result">
        {(site.data.legalName || siteVat || site.data.legalAddress) && <div className="local-site-identity"><span>Identità rilevata</span>{site.data.legalName && <strong>{site.data.legalName}</strong>}{siteVat && <p>P.IVA {siteVat}{site.data.vatValid != null && <small className={site.data.vatValid ? 'is-valid' : 'is-invalid'}>{site.data.vatValid ? 'VIES valida' : 'VIES non valida'}</small>}</p>}{site.data.legalAddress && <small>{site.data.legalAddress}</small>}</div>}
        {techSignals.length > 0 && <div className="local-tech-signals"><span>Pixel pubblicitari rilevati</span><div>{techSignals.map((signal) => <strong key={signal}>{signal}</strong>)}</div></div>}
        <dl>{site.data.pricing && <div><dt>Prezzi e piani</dt><dd>{site.data.pricing}</dd></div>}{site.data.features && <div><dt>Offerta</dt><dd>{site.data.features}</dd></div>}{site.data.socialLinks && <div><dt>Profili social</dt><dd>{site.data.socialLinks}</dd></div>}</dl>
        {!site.data.pricing && !site.data.features && !site.data.socialLinks && techSignals.length === 0 && !siteVat && <p className="local-honest-state">Il sito è raggiungibile, ma non contiene segnali pubblici sufficienti nelle sezioni analizzate.</p>}
        <p className="local-result-source">Sito pubblico analizzato su richiesta · aggiornato {new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(site.data.fetchedAt))}</p>
      </div>}
    </section>

    <section className="local-detail-section local-company-section" aria-labelledby="local-company-title">
      <header><div><p className="eyebrow">Registro Imprese</p><h2 id="local-company-title">Dati societari</h2></div>{vat && <button type="button" className="btn-secondary" disabled={company.isPending} onClick={() => company.mutate(vat)}>{company.isPending ? 'Completo i dati…' : company.data ? cachedCompanyProfile ? 'Aggiorna indicatori' : 'Aggiorna dati · ~€0,10' : cachedCompanyProfile ? 'Completa gli indicatori' : 'Recupera · ~€0,10'}</button>}</header>

      {!siteVat && !confirmedVat && <div className="local-registry-search">
        <div className="local-registry-intro"><div><strong>Trova la ragione sociale corretta</strong><p>La scheda locale non espone la P.IVA. Cerca le imprese con nome simile nella zona e scegli manualmente quella giusta.</p></div><span>~€0,01</span></div>
        <button type="button" className="btn-secondary local-registry-trigger" disabled={registry.isPending} onClick={() => registry.mutate()}>{registry.isPending ? 'Cerco nel Registro…' : registry.data ? 'Ripeti la ricerca · ~€0,01' : 'Cerca nel Registro Imprese · ~€0,01'}</button>
        {registry.isPending && <div className="local-detail-loading" role="status"><span className="sr-only">Ricerca nel Registro Imprese</span><i/><i/><i/></div>}
        {registry.isError && <div className="notice notice-error"><p>{errorMessage(registry.error)}</p><button type="button" className="btn-secondary" onClick={() => registry.mutate()}>Riprova</button></div>}
        {registry.isSuccess && registryCandidates.length === 0 && <p className="local-registry-empty">Nessuna azienda con questo nome nel registro della zona/provincia.</p>}
        {registry.isSuccess && registryCandidates.length > 1 && <p className="local-registry-warning" role="status"><strong>Verifica necessaria</strong><span>Ci sono più aziende con nome simile: verifica prima di continuare.</span></p>}
        {registry.isSuccess && needsActivityVerification && <p className="local-registry-verification-note">Confronta prima la sede. Se non basta, puoi verificare l’attività ATECO dichiarata di ogni candidata: la verifica non effettua alcuna selezione.</p>}
        {registry.isSuccess && weakRegistryMatch && <p className="local-registry-warning is-weak"><strong>Corrispondenza debole</strong><span>Nessun risultato somiglia abbastanza al nome cercato. Controlla con particolare attenzione città e P.IVA.</span></p>}
        {registryCandidates.length > 0 && <div className="local-company-candidates" role="list" aria-label="Aziende candidate nel Registro Imprese">
          {registryCandidates.map((candidate) => <RegistryCandidateCard
            key={candidate.vat}
            candidate={candidate}
            selected={selectedCandidate?.vat === candidate.vat}
            allowActivityCheck={needsActivityVerification && candidate.score >= .5}
            cachedProfile={candidateProfiles[candidate.vat]}
            onVerified={(profile) => setCandidateProfiles((current) => ({ ...current, [candidate.vat]: profile }))}
            onSelect={() => { setSelectedCandidate(candidate); setConfirmedVat(null); company.reset(); }}
          />)}
        </div>}
        {selectedCandidate && <div className="local-registry-confirm"><div><strong>Conferma questa impresa?</strong><p>{selectedCandidate.companyName} · P.IVA {selectedCandidate.vat}</p>{candidateProfiles[selectedCandidate.vat]?.atecoDescription && <small className="local-confirmed-activity">Attività: {candidateProfiles[selectedCandidate.vat].atecoDescription}</small>}</div><button type="button" className="btn-primary" onClick={confirmSelectedCompany}>Conferma questa P.IVA</button></div>}
      </div>}

      {!siteVat && confirmedVat && <div className="local-confirmed-company"><span aria-hidden="true">✓</span><div><strong>{selectedCandidate?.companyName ?? rememberedCompany?.companyName ?? `P.IVA ${confirmedVat}`}</strong>{selectedCandidate && <p>{[selectedCandidate.street, selectedCandidate.town, selectedCandidate.registeredYear ? `iscritta dal ${selectedCandidate.registeredYear}` : null].filter(Boolean).join(' · ')}</p>}{candidateProfiles[confirmedVat]?.atecoDescription && <p>Attività: {candidateProfiles[confirmedVat].atecoDescription}</p>}<small>P.IVA confermata e ricordata: {confirmedVat}</small></div><button type="button" onClick={changeConfirmedCompany}>Cambia</button></div>}
      {vat && !company.data && !company.isPending && <div className="local-cost-note"><strong>{cachedCompanyProfile ? 'Profilo già verificato' : 'Azione a pagamento'}</strong><p>{cachedCompanyProfile ? 'Riutilizziamo il profilo ATECO appena recuperato: completare gli indicatori non richiede di nuovo la stessa anagrafica.' : 'La richiesta completa costa indicativamente €0,10. Non parte automaticamente.'}</p></div>}
      {company.isPending && <div className="local-detail-loading" role="status"><span className="sr-only">Recupero dati societari</span><i/><i/><i/></div>}
      {company.isError && vat && <div className="notice notice-error"><p>{errorMessage(company.error)}</p><button type="button" className="btn-secondary" onClick={() => company.mutate(vat)}>Riprova</button></div>}
      {company.data && <div className="local-company-result">
        <div className="local-company-summary"><div><span>Dimensione</span><strong>{company.data.indicators.dimensione || 'n/d'}</strong><small>{company.data.indicators.fasciaFatturato || 'Fascia non disponibile'}</small></div><div><span>Solidità</span><strong className={company.data.indicators.solidita ? `is-${company.data.indicators.solidita}` : undefined}>{company.data.indicators.soliditaLabel || 'n/d'}</strong><small>{company.data.indicators.tendenza || 'Tendenza non disponibile'}</small></div></div>
        <dl><div><dt>Impresa</dt><dd>{company.data.profile.companyName || 'n/d'}</dd></div><div><dt>Forma giuridica</dt><dd>{company.data.profile.legalForm || 'n/d'}</dd></div><div><dt>Fatturato</dt><dd>{company.data.profile.turnover != null ? currency.format(company.data.profile.turnover) : 'n/d'}</dd></div><div><dt>Dipendenti</dt><dd>{company.data.profile.employees != null ? integer.format(company.data.profile.employees) : 'n/d'}</dd></div><div><dt>ATECO</dt><dd>{[company.data.profile.atecoCode, company.data.profile.atecoDescription].filter(Boolean).join(' · ') || 'n/d'}</dd></div><div><dt>Bilancio</dt><dd>{company.data.profile.balanceYear ?? 'n/d'}</dd></div></dl>
        <CompanyHistory indicators={company.data.indicators}/>
        <p className="local-result-source">{company.data.indicators.fonte}. Valori grezzi visibili soltanto in questa vista in-app.</p>
      </div>}
    </section>

    {vat ? <OfficialDocuments vat={vat}/> : <OfficialDocumentsWithoutVat/>}
    <CatastoProperty address={address} activityName={name}/>

    <section className="local-report-dossier" aria-labelledby="local-report-title">
      <header><span className="local-report-icon"><DossierIcon/></span><div><p className="eyebrow">Dossier dell’attività</p><h2 id="local-report-title">Crea un report PDF completo</h2><p>Più informazioni sblocchi su sito, società e annunci, più ricco sarà il dossier.</p></div></header>
      <ul className="local-report-coverage" aria-label="Informazioni disponibili per il dossier">
        <li className={site.data ? 'is-ready' : undefined}><span aria-hidden="true">{site.data ? '✓' : '○'}</span><div><strong>Sito</strong><small>{site.data ? 'Analisi inclusa' : website ? 'Puoi ancora analizzarlo' : 'Non disponibile'}</small></div></li>
        <li className={vat ? 'is-ready' : undefined}><span aria-hidden="true">{vat ? '✓' : '○'}</span><div><strong>Società</strong><small>{vat ? 'P.IVA identificata' : 'P.IVA non sbloccata'}</small></div></li>
        <li className={metaMatch ? 'is-ready' : undefined}><span aria-hidden="true">{metaMatch ? '✓' : '○'}</span><div><strong>Annunci</strong><small>{metaMatch ? `${integer.format(metaMatch.adsCount)} rilevati` : 'Nessun match certo'}</small></div></li>
        <li className={targetId || reportCompetitors ? 'is-ready' : undefined}><span aria-hidden="true">{targetId || reportCompetitors ? '✓' : '○'}</span><div><strong>Territorio</strong><small>{reportCompetitors ? `${integer.format(reportCompetitors.length)} ${reportCompetitors.length === 1 ? 'rivale incluso' : 'rivali inclusi'}` : targetId ? 'Zona collegata' : 'Zona attiva predefinita'}</small></div></li>
      </ul>
      <fieldset className="local-report-lens" disabled={dossier.isPending}>
        <legend>Che decisione stai prendendo?</legend>
        <div className="local-report-lens-options">
          <label className={`local-report-lens-option${reportLens === 'acquisizione' ? ' is-selected' : ''}`}><input type="radio" name="report-lens" value="acquisizione" checked={reportLens === 'acquisizione'} onChange={() => chooseReportLens('acquisizione')}/><span><strong>Studiare questa attività</strong><small>Il dossier parte dall’attività, dalla società e dai suoi annunci.</small></span></label>
          <label className={`local-report-lens-option${reportLens === 'apertura' ? ' is-selected' : ''}`}><input type="radio" name="report-lens" value="apertura" checked={reportLens === 'apertura'} onChange={() => chooseReportLens('apertura')}/><span><strong>Valutare se aprire in questa zona</strong><small>Il dossier parte dal territorio e dal panorama competitivo.</small></span></label>
        </div>
        <p className={`local-report-lens-hint${reportLens ? ' is-ready' : ''}`}>{reportLens ? 'Scelta applicata al prossimo PDF.' : 'Scegli una delle due opzioni per generare il PDF.'}</p>
      </fieldset>
      <div className="local-report-actions"><button type="button" className="btn-primary" disabled={dossier.isPending || !reportLens} onClick={createDossier}>{dossier.isPending ? <><span className="local-report-progress" aria-hidden="true"><i/><i/><i/></span>Genero il dossier…</> : dossier.data ? 'Crea un nuovo PDF' : 'Crea report PDF'}</button><small>File PDF generato dal server · in genere 3–10 secondi</small></div>
      {dossier.isError && <div className="notice notice-error local-report-message"><p>{errorMessage(dossier.error)}</p><button type="button" className="btn-secondary" onClick={createDossier}>Riprova</button></div>}
      {dossier.isSuccess && <div className="local-report-success" role="status"><span aria-hidden="true">✓</span><div><strong>Dossier creato</strong><p>Il download è iniziato. Il file resta disponibile finché mantieni aperta questa pagina.</p></div><button type="button" onClick={() => saveDownloadedFile(dossier.data)}>Scarica di nuovo</button></div>}
    </section>
  </article>;
}
